"use client";
import { LocaleAttribute } from "@/app/i18n/settings";
import { getBaseUrl } from "@/util/get-base-url";
import { formatCurrencyFormat } from "@/util/formatCurrencyFormat";
import { toPriceString } from "@/util/toPriceString";
import { CUSTOMER_LEVEL } from "@prisma/client";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CollectionListItemClient from "@/components/collections/collection-list-favorite-client"
import "./product-showboxes.css";
import ProductTagRenderer from "./product-tag-renderer";

interface ProductShowboxesCarouselProps {
    productGroupItems: any;
    productList: any[];
    priorityImage: boolean;
    colorStyle: any;
    loc: LocaleAttribute;
    shopCurrency?: any;
    level: CUSTOMER_LEVEL;
    text: any;
    groupId: number;
    radiusStyle: any;
}

const ProductShowboxesCarousel = (props: ProductShowboxesCarouselProps) => {
    const { productGroupItems, productList, priorityImage, colorStyle, loc, shopCurrency, level, text, groupId, radiusStyle } = props;
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });

    const scrollNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };
    const scrollPrev = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    return (

            <div id="product-showboxes-component-carousel-container" className="embla">
                <div id="product-showboxes-component-carousel-embla" className="embla" ref={emblaRef}>
                    <div id="product-showboxes-component-carousel-embla-container" className="embla__container" style={{ display: "flex", gap: "16px"}}>
                        {productList.map((product: any) => (
                            (product && product.productVariant && product.productVariant.length > 0) && (
                            <div 
                                id={`product-showboxes-component-carousel-item-${product.id}`}
                                key={product.id} 
                                className="product-showboxes flex flex-col gap-5 flex-shrink-0"
                            >
                                <div id={`product-showboxes-component-carousel-item-content-${product.id}`} className="flex flex-col gap-6 justify-start items-start w-full h-full">
                                    <div
                                        id={`product-showboxes-component-carousel-item-img-${product.id}`}
                                        className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480"
                                        style={{ maxWidth: "100%" }}
                                    >
                                        <a id={`product-showboxes-component-carousel-item-img-link-${product.id}`} href={`/products/${product.handle}`} className="w-full h-full">
                                            <picture id={`product-showboxes-component-carousel-img-${product.id}`}>
                                                <source
                                                    srcSet={product.featuredImageUrl != "" ? `${getBaseUrl(product.featuredImageUrl || "")}.avif` : "/assets/images/demo/web_logo_white.svg"}
                                                    type="image/avif"
                                                />
                                                <source
                                                    srcSet={product.featuredImageUrl != "" ? `${getBaseUrl(product.featuredImageUrl || "")}.webp` : "/assets/images/demo/web_logo_white.svg"}
                                                    type="image/webp"
                                                />
                                                <img
                                                    src={product.featuredImageUrl != "" ? product.featuredImageUrl : "/assets/images/demo/web_logo_white.svg"}
                                                    className={`w-full h-full object-contain bg-white rounded-3xl`}
                                                    width={480}
                                                    height={480}
                                                    loading={priorityImage ? "eager" : "lazy"}
                                                    alt={product.handle + "-image"}
                                                />
                                            </picture>
                                        </a>
                                        <CollectionListItemClient productHandle={product.handle} refresh={true} />
                                    </div>
                                    <div id={`product-showboxes-component-carousel-item-details-${product.id}`} className="flex flex-col gap-5 justify-between w-full h-full">
                                        <div id={`product-showboxes-component-carousel-item-name-${product.id}`} className="flex flex-col gap-1 w-full ">
                                            <a  href={`/products/${product.handle}`}
                                                className="font-semibold xl:text-lg text-sm leading-8 hover:opacity-50"
                                                style={{
                                                    color: colorStyle ? colorStyle.text : "#3d3d3d"
                                                }}>
                                                <span className="line-clamp-2">{product.attributes.displayName || ""}</span>
                                            </a>
                                        </div>
                                        <ProductTagRenderer tags={product.tags} iconOnly />
                                        {product.productVariant[0].show_price ? (
                                            <>
                                                {level == "BP" &&
                                                    <div id={`product-showboxes-component-carousel-item-price-${product.id}`} className="flex flex-row items-center flex-wrap gap-2 min-h-8">
                                                        <span id={`product-showboxes-component-carousel-item-price-special-${product.id}`}
                                                            className="font-semibold xl:text-2xl lg:text-xl text-lg leading-8"
                                                            style={{
                                                                color: colorStyle ? colorStyle.text : "#3d3d3d"
                                                            }}
                                                        >{formatCurrencyFormat(toPriceString(product.productVariant[0].comparePriceTrial), shopCurrency)}</span>
                                                        
                                                        {product.productVariant[0].priceTrial != product.productVariant[0].comparePriceTrial && 
                                                            <span 
                                                                id={`product-showboxes-component-carousel-item-price-normal-${product.id}`}
                                                                className="line-through font-normal xl:text-base lg:text-sm text-xs leading-8"
                                                                style={{
                                                                    color: colorStyle ? colorStyle.text : "#3d3d3d",
                                                                    opacity: 0.5
                                                                }}
                                                            >
                                                                {formatCurrencyFormat(toPriceString(product.productVariant[0].priceTrial), shopCurrency)}
                                                            </span>
                                                        }
                                                    </div>
                                                }
                                                {(level == "NORMAL" || level == "GUEST") &&
                                                    <div id={`product-showboxes-component-carousel-item-price-${product.id}`} className="flex flex-row items-center flex-wrap gap-2 min-h-8">
                                                        <span id={`product-showboxes-component-carousel-item-price-special-${product.id}`}
                                                            className="font-semibold xl:text-2xl lg:text-xl text-lg leading-8"
                                                            style={{
                                                                color: colorStyle ? colorStyle.text : "#3d3d3d"
                                                            }}
                                                        >{formatCurrencyFormat(toPriceString(product.productVariant[0].comparePriceFull), shopCurrency)}</span>
                                                        {product.productVariant[0].priceFull != product.productVariant[0].comparePriceFull && 
                                                            <span 
                                                                id={`product-showboxes-component-carousel-item-price-normal-${product.id}`}
                                                                className="line-through font-normal text-base leading-8"
                                                                style={{
                                                                    color: colorStyle ? colorStyle.text : "#3d3d3d",
                                                                    opacity: 0.5
                                                                }}
                                                            >
                                                                {formatCurrencyFormat(toPriceString(product.productVariant[0].priceFull), shopCurrency)}
                                                            </span>
                                                        }
                                                    </div>
                                                }
                                            </>
                                        ) : (
                                            <div className="min-h-8"></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            )
                        ))}
                    </div>
                </div>
                <div id={`product-showboxes-component-carousel-item-actions`} className="w-full flex justify-between items-center">
                    {productList.length > 4 && (<div className="flex w-1/3 justify-end mt-[40px]"></div>)}
                    {(productGroupItems[groupId].buttonAttributes 
                        && productGroupItems[groupId].enableButton
                    ) && (
                        <div id={`product-showboxes-component-carousel-item-button`} className="w-full flex justify-center mt-[40px]">
                            <a href={productGroupItems[groupId].buttonAttributes.linkUrl || ""} aria-label="button">
                                <button
                                    className="min-w-12 min-h-12 w-fit h-fit py-4 px-10 rounded-full font-medium px-10 dynamic-bg dynamic-color"
                                    style={
                                        {
                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#006ef2",
                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                            borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                            borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                            borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                            borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                        } as React.CSSProperties
                                    }>
                                    {productGroupItems[groupId].buttonAttributes.displayName == "" ?  text?.see_all_button : productGroupItems[groupId].buttonAttributes.displayName}
                                </button>
                            </a>
                        </div>
                    )}   
                    {productList.length > 4 && (
                    <div id="product-showboxes-component-carousel-prev-next" className="flex w-1/3 justify-center mt-[40px]">
                        <button
                            aria-label="Previous" 
                            className="
                                embla__button embla__button--prev inline-flex items-center justify-center 
                                whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                                relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                            style={
                                {
                                    "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                    "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                    "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                } as React.CSSProperties
                            }
                            onClick={scrollPrev}>
                            <ChevronLeft 
                                className="h-5 w-5"
                            />
                        </button>
                        <button
                            aria-label="Next"
                            className="
                                embla__button embla__button--next inline-flex items-center justify-center 
                                whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                                relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                            style={
                                {
                                    "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                    "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                    "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                } as React.CSSProperties
                            }
                            onClick={scrollNext}>
                            <ChevronRight 
                                className="h-5 w-5"
                            />
                        </button>
                    </div>
                    )}
                </div>
            </div>
    )
}

export default ProductShowboxesCarousel;